import { Component, inject, OnInit, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, take } from 'rxjs/operators';

import { BaseFormControl } from '../base-control';
import { HttpClientWithInFlightCache } from '../../../global/httpClient';
import { currencyForCountry, timeZoneForCountry } from '../../../event/location-defaults';

/** Mirrors the VenueResult the API returns; kept local so the control owns its contract. */
interface VenueResult {
    name: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    countryCode: string;
    lat: number;
    lng: number;
}

@Component({
    standalone: false,
    selector: 'abs-venue-search',
    template: `
        <!-- .form-group is the inset every other field on this form sits in.
             Without it this control's input sat hard against the page edge while
             its label was 8px in and its notes 24px in — three different left
             edges inside one field. -->
        <div class="form-group venue-search">
            <label>{{ field.label || 'Venue' }}</label>
            <ion-input placeholder="Search for a venue by name" [value]="queryText()" (ionInput)="onQuery($any($event).target.value)">
            </ion-input>

            @if (searching()) {
                <p class="venue-search-note">Searching…</p>
            }

            @if (results().length) {
                <ion-list class="venue-search-results">
                    @for (result of results(); track result.address) {
                        <ion-item button (click)="choose(result)">
                            <ion-label>
                                <h3>{{ result.name }}</h3>
                                <p>{{ result.address }}</p>
                            </ion-label>
                        </ion-item>
                    }
                </ion-list>
            }

            @if (searched() && !searching() && !results().length) {
                <p class="venue-search-note">No venues found. You can enter the address manually below.</p>
            }

            @if (chosenAddress()) {
                <p class="venue-search-note">Selected: {{ chosenAddress() }}</p>
            }
        </div>
    `
})
export class VenueSearchComponent extends BaseFormControl implements OnInit {
    private http = inject(HttpClientWithInFlightCache);

    readonly queryText = signal('');
    readonly results = signal<VenueResult[]>([]);
    readonly searching = signal(false);
    readonly searched = signal(false);
    readonly chosenAddress = signal('');

    private query$ = new Subject<string>();

    // No constructor: BaseFormControl's takes five injected arguments, and
    // parentFormGroup is only assigned during its ngOnInit.
    ngOnInit() {
        super.ngOnInit();
        this.showSavedVenue();

        this.query$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((q) => {
                    if (!q?.trim()) {
                        this.searching.set(false);
                        return of([] as VenueResult[]);
                    }
                    this.searching.set(true);
                    // Bias to the country the form already holds, when it has one.
                    // Without it a search for a venue name can rank a same-named place
                    // on another continent above the local one — "Bag of Holding"
                    // returns a Texas shop above the Nottingham one.
                    const country = (this.parentFormGroup.root as UntypedFormGroup).controls['location.countryCode']?.value;
                    const url =
                        `${this.config.apiBaseUrl}/events/venue-search?q=${encodeURIComponent(q)}` +
                        (country ? `&country=${encodeURIComponent(country)}` : '');
                    // A failed lookup must leave the organiser with manual entry, never an error state.
                    return this.http.get<VenueResult[]>(url, { requiresLogin: true }).pipe(catchError(() => of([] as VenueResult[])));
                })
            )
            .subscribe((results) => {
                this.searching.set(false);
                // Only a live, non-empty query can have "found nothing". Clearing the
                // box must not leave the empty-state message sitting under it.
                this.searched.set(!!this.queryText().trim());
                this.results.set(results ?? []);
            });
    }

    /**
     * Shows the event's saved venue in the search box when editing, instead of an
     * empty field beside an address that already names one.
     *
     * The value is read from the form rather than held here, and setting queryText
     * does not start a search — only onQuery does — so this fills the box without
     * calling out to Mapbox.
     *
     * patchForm runs after this component initialises, so the control is usually
     * still empty right now; hence also taking the first value it emits. Applied
     * only while the box is empty, so it can never overwrite something the
     * organiser has started typing.
     */
    private showSavedVenue() {
        const venueName = (this.parentFormGroup.root as UntypedFormGroup).controls['location.venueName'];
        if (!venueName) {
            return;
        }

        if (venueName.value) {
            this.queryText.set(venueName.value);
        }

        venueName.valueChanges.pipe(take(1)).subscribe((value) => {
            if (value && !this.queryText()) {
                this.queryText.set(value);
            }
        });
    }

    onQuery(value: string) {
        this.queryText.set(value);
        this.query$.next(value);
    }

    /**
     * Writes onto the root form group's controls. The form builds controls with each
     * field's name as a literal key (base-form.config.ts: `agg[item.name] = new
     * UntypedFormControl(...)`), so 'location.city' is one flat control literally
     * keyed 'location.city' — not a nested 'location' group. `AbstractControl.get()`
     * splits a string argument on '.', so `root.get('location.city')` would walk into
     * a non-existent 'location' child and silently return null. `root.controls[key]`
     * indexes the literal key directly and must be used instead. Currency and
     * timezone are filled only when empty, so a deliberate choice by the organiser is
     * never overwritten.
     */
    choose(result: VenueResult) {
        const root = this.parentFormGroup.root as UntypedFormGroup;

        const patch: Record<string, any> = {
            'location.venueName': result.name,
            'location.city': result.city,
            'location.state': result.state,
            'location.postalCode': result.postalCode,
            'location.countryCode': result.countryCode,
            'location.coordinates': { type: 'Point', coordinates: [result.lng, result.lat] }
        };

        const currency = currencyForCountry(result.countryCode);
        if (currency && !root.controls['currency']?.value) {
            patch['currency'] = currency;
        }

        // Country first, then the browser's own zone — location-defaults.ts explains
        // why a country spanning several zones deliberately has no mapping.
        const timeZone = timeZoneForCountry(result.countryCode) || Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timeZone && !root.controls['timeZone']?.value) {
            patch['timeZone'] = timeZone;
        }

        for (const [key, value] of Object.entries(patch)) {
            root.controls[key]?.setValue(value);
        }

        this.chosenAddress.set(result.address);
        this.results.set([]);
        this.queryText.set(result.name);
        // The search is over and it succeeded. Without this the empty result list
        // reads as "found nothing" and the failure message appears under a venue the
        // organiser just successfully picked.
        this.searched.set(false);
    }
}
